import { useEffect } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import { getFormFields, getInitialValues } from '../../functions';
import { Text } from '../../helpers';
import { IInvoice } from '../../interfaces';
import { setGlobalState, useGlobalState } from '../../state';
import AnimatedLoader from '../AnimatedLoader/AnimatedLoader';
import CustomForm from '../CustomForm/CustomForm';
import RoundBox from '../RoundBox/RoundBox';
import Address from './Address';
import PaymentProviderFooter from './PaymentProviderFooter';
import { approveCopy, resumeStripePayment } from '../../functions/approveCopy';
import Button from '../Button/Button';
import { useTranslation } from 'react-i18next';
import { colors } from '../../assets/colors';
import signInvoice, { buildSignInvoicePayload } from '../../api/signInvoice/signInvoice';

type InvoiceUserInfoPropType = {
    invoice: IInvoice;
    setShowInfo: React.Dispatch<React.SetStateAction<'manual' | 'confirmed' | 'start'>>;
    loading: boolean;
};

const Wrapper = styled.div``;
const InvoiceUserInfo = ({ invoice, setShowInfo, loading }: InvoiceUserInfoPropType) => {
    const { t } = useTranslation();
    const [optionStatus] = useGlobalState('optionStatus');
    const canSign = !optionStatus || optionStatus === 'approved';
    const copy = approveCopy(invoice.payment_provider);
    const resumePayment = resumeStripePayment(invoice);

    useEffect(() => {
        for (const key in invoice.customer) {
            if (
                key === 'phone_number' ||
                key === 'care_of' ||
                key === 'id_number' ||
                key === 'country'
            ) {
                continue;
            } else if (invoice.customer[key as keyof IInvoice['customer']] === '') {
                setShowInfo('manual');
                break;
            }
        }
    }, []);

    const InvoiceInvoiceUserInfoSchema = Yup.object({
        email: Yup.string().email('Invalid email').required('Required'),
        phone_number: Yup.number()
    });

    const initialValues = getInitialValues(invoice.customer);

    const onSubmit = async (values: Partial<IInvoice['customer']>) => {
        setGlobalState('loading', true);
        if (loading) {
            return;
        }
        await signInvoice(
            invoice.uuid,
            buildSignInvoicePayload(invoice, {
                overrides: { email: values.email, phone_number: values.phone_number },
                ...(resumePayment ? { needBankSignature: false } : {})
            }),
            invoice.payment_provider
        );
        setGlobalState('loading', false);
    };
    return (
        <Wrapper>
            {loading && <AnimatedLoader />}
            <RoundBox style={{ padding: '16px' }}>
                <Text
                    i={t('invoiceInformation')}
                    fontWeight="normal"
                    style={{ fontSize: '18px', marginBottom: '13px' }}
                />
                <Address setShowInfo={setShowInfo} />
                <CustomForm
                    initialValues={initialValues}
                    validationSchema={InvoiceInvoiceUserInfoSchema}
                    onFormSubmit={onSubmit}
                    fields={getFormFields(invoice.customer)}
                    buttonText={
                        resumePayment ? t('continueToPayment') : t(copy.withBankID ?? 'complete')
                    }
                    showBankIdLogo={!resumePayment}
                    disabled={!canSign}
                />

                {!invoice.is_signing_required && !resumePayment && (
                    <Button
                        i={t(copy.withoutBankID)}
                        color={colors.white}
                        fontWeight="bold"
                        style={{
                            marginTop: '8%',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '15px'
                        }}
                        disabled={loading || !canSign}
                        onClick={async () => {
                            setGlobalState('loading', true);
                            await signInvoice(
                                invoice.uuid,
                                buildSignInvoicePayload(invoice, { needBankSignature: false }),
                                invoice.payment_provider
                            );
                            setGlobalState('loading', false);
                        }}
                    />
                )}

                <PaymentProviderFooter invoice={invoice} showDivider />
            </RoundBox>
        </Wrapper>
    );
};

export default InvoiceUserInfo;
